import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notificationRoleLabel } from '../i18n/notification_roles'
import { useUpdateTemplate } from '../hooks/useTemplates'
import type { NotificationTemplateRow } from '@/api/notifications'
import { USE_MOCK } from '@/config'

const VARIABLES = ['{brand}', '{model}', '{order_number}', '{customer_company}', '{executor_company}', '{address_city}']

interface Props {
  row: NotificationTemplateRow
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

export function RoleRow({ row }: Props) {
  const { t } = useTranslation()
  const [title, setTitle] = useState(row.title ?? '')
  const [body, setBody] = useState(row.body ?? '')
  const [isEnabled, setIsEnabled] = useState(row.is_enabled)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [retryPayload, setRetryPayload] = useState<Partial<NotificationTemplateRow> | null>(null)
  const [titleFocused, setTitleFocused] = useState(false)
  const [bodyFocused, setBodyFocused] = useState(false)

  const titleRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updateMut = useUpdateTemplate()

  const doSave = useCallback(
    async (patch: Partial<NotificationTemplateRow>) => {
      setSaveStatus('saving')
      setErrorMsg(null)
      try {
        await updateMut.mutateAsync({ event_type: row.event_type, role: row.role, ...patch })
        setSaveStatus('saved')
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
        savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000)
      } catch (e) {
        setSaveStatus('error')
        setErrorMsg(e instanceof Error ? e.message : String(e))
        setRetryPayload(patch)
      }
    },
    [row.event_type, row.role, updateMut],
  )

  const scheduleSave = useCallback(
    (patch: Partial<NotificationTemplateRow>, delay = 500) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => void doSave(patch), delay)
    },
    [doSave],
  )

  function handleTitleChange(val: string) {
    setTitle(val)
    scheduleSave({ title: val })
  }

  function handleBodyChange(val: string) {
    setBody(val)
    scheduleSave({ body: val })
    // Autoresize
    if (bodyRef.current) {
      bodyRef.current.style.height = 'auto'
      bodyRef.current.style.height = `${bodyRef.current.scrollHeight}px`
    }
  }

  function handleEnabledChange(checked: boolean) {
    setIsEnabled(checked)
    scheduleSave({ is_enabled: checked }, 0)
  }

  function insertVariable(variable: string, target: 'title' | 'body') {
    if (target === 'title' && titleRef.current) {
      const el = titleRef.current
      const start = el.selectionStart ?? title.length
      const end = el.selectionEnd ?? title.length
      const next = title.slice(0, start) + variable + title.slice(end)
      setTitle(next)
      scheduleSave({ title: next })
      requestAnimationFrame(() => {
        el.focus()
        el.setSelectionRange(start + variable.length, start + variable.length)
      })
    } else if (target === 'body' && bodyRef.current) {
      const el = bodyRef.current
      const start = el.selectionStart ?? body.length
      const end = el.selectionEnd ?? body.length
      const next = body.slice(0, start) + variable + body.slice(end)
      setBody(next)
      scheduleSave({ body: next })
      requestAnimationFrame(() => {
        el.focus()
        el.setSelectionRange(start + variable.length, start + variable.length)
      })
    }
  }

  const showVarBar = titleFocused || bodyFocused
  const activeTarget: 'title' | 'body' = titleFocused ? 'title' : 'body'

  return (
    <div className="rounded-lg border border-[color:var(--stryzh-color-border-subtle)] bg-[color:var(--stryzh-color-surface-raised)] p-3">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id={`enabled-${row.event_type}-${row.role}`}
          checked={isEnabled}
          onChange={(e) => handleEnabledChange(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[color:var(--stryzh-color-accent-orange)]"
          disabled={USE_MOCK}
        />
        <div className="flex-1 min-w-0">
          <label
            htmlFor={`enabled-${row.event_type}-${row.role}`}
            className="mb-2 block text-sm font-medium text-[color:var(--stryzh-color-text-primary)] cursor-pointer"
          >
            {notificationRoleLabel(row.role)}
          </label>

          {showVarBar && (
            <div className="mb-2 flex flex-wrap gap-1">
              {VARIABLES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    insertVariable(v, activeTarget)
                  }}
                  className="rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] px-2 py-0.5 font-mono text-xs text-[color:var(--stryzh-color-text-secondary)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                >
                  {v}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <input
              ref={titleRef}
              type="text"
              value={title}
              maxLength={100}
              placeholder={t('notif.roleRow.titlePlaceholder')}
              onFocus={() => setTitleFocused(true)}
              onBlur={() => setTitleFocused(false)}
              onChange={(e) => handleTitleChange(e.target.value)}
              disabled={USE_MOCK}
              className="w-full rounded-[var(--stryzh-radius-lg)] border border-[color:var(--stryzh-color-border-default)] bg-transparent px-2 py-1.5 text-sm text-[color:var(--stryzh-color-text-primary)] placeholder:text-[color:var(--stryzh-color-text-secondary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
            />
            <textarea
              ref={bodyRef}
              value={body}
              maxLength={500}
              placeholder={t('notif.roleRow.bodyPlaceholder')}
              rows={2}
              onFocus={() => setBodyFocused(true)}
              onBlur={() => setBodyFocused(false)}
              onChange={(e) => handleBodyChange(e.target.value)}
              disabled={USE_MOCK}
              className="w-full resize-none overflow-hidden rounded-[var(--stryzh-radius-lg)] border border-[color:var(--stryzh-color-border-default)] bg-transparent px-2 py-1.5 text-sm text-[color:var(--stryzh-color-text-primary)] placeholder:text-[color:var(--stryzh-color-text-secondary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
            />
          </div>

          <div className="mt-1 h-4 text-xs">
            {saveStatus === 'saving' && (
              <span className="text-[color:var(--stryzh-color-text-secondary)]">{t('notif.roleRow.saving')}</span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-[color:var(--stryzh-color-accent-orange)]">{t('notif.roleRow.saved')}</span>
            )}
            {saveStatus === 'error' && (
              <span className="text-[color:var(--stryzh-color-status-error)]">
                {errorMsg ?? t('notif.roleRow.saveError')}
                {retryPayload && (
                  <button
                    type="button"
                    onClick={() => void doSave(retryPayload)}
                    className="ml-2 underline"
                  >
                    {t('notif.common.retry')}
                  </button>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

